/**
 * Report › Dashboard (2.33, decided 22/10/2026): the revenue of a year or a
 * quarter against the same period of the year before, and what is left to
 * collect and to pay at the end of the period, by how late it is.
 *
 * An economic report (2.34): every amount without VAT. Receivables and
 * payables are the part of the taxable still open, in proportion to what is
 * left to collect or to pay. The purchases follow the rules of the revenue on
 * the received invoices. The debts to the banks (invoice advances, loans) come
 * apart, as they are: financial debts, not costs.
 *
 * - Revenue: taxable of the issued invoices less the credit notes, by the date
 *   of issue. Out: cancelled invoices, pro forma, self-billing and items of the
 *   situation at 1 January (they are not revenue of the year).
 * - Receivables: what is left of the issued invoices and credit notes.
 * - Payables: what is left of the received invoices, credit notes and pro forma
 *   not replaced: everything that can be asked for. Self-billing is out (the
 *   reverse-charge integrations are not paid to anyone).
 * - Open at a day: the net due less the payments up to that day, as in the
 *   Quadratura. The payments cover the dues from the oldest, so what is left
 *   belongs to the latest dues.
 */
import { z } from 'zod';
import { addDays, parseIsoDate } from './dates.js';
import { fromCents, toCents } from './money.js';
export const DASHBOARD_QUARTERS = [1, 2, 3, 4];
export const dashboardQuerySchema = z.object({
    year: z.coerce.number().int().min(2000).max(2100),
    /** Empty: the whole year. */
    quarter: z.coerce
        .number()
        .int()
        .min(1)
        .max(4)
        .optional()
        .transform((value) => value),
    /** «include»: the intergroup invoices count too (3.18). */
    intercompany: z.enum(['include']).optional(),
});
/** Overdue days: not yet due, then 1-30, 31-60, 61-90 and over 90. */
export const AGING_BUCKETS = ['notDue', 'd30', 'd60', 'd90', 'over90'];
export const AGING_LABELS = {
    notDue: 'A scadere',
    d30: 'Scaduti da 1–30 gg',
    d60: '31–60 gg',
    d90: '61–90 gg',
    over90: 'Oltre 90 gg',
};
/** First and last day of a year or of one of its quarters. */
export function dashboardPeriod(year, quarter) {
    const first = quarter ? (quarter - 1) * 3 + 1 : 1;
    const last = quarter ? first + 2 : 12;
    const pad = (value) => String(value).padStart(2, '0');
    const end = addDays(last === 12 ? `${year + 1}-01-01` : `${year}-${pad(last + 1)}-01`, -1);
    return {
        from: `${year}-${pad(first)}-01`,
        to: end,
        months: Array.from({ length: last - first + 1 }, (_, index) => first + index),
    };
}
/** The same day a year before; 29 February becomes 28. */
function yearBefore(day) {
    const [year, month, date] = day.split('-');
    return `${Number(year) - 1}-${month}-${month === '02' && date === '29' ? '28' : date}`;
}
function isEconomic(direction) {
    return (invoice) => invoice.direction === direction &&
        (invoice.kind === 'fattura' || invoice.kind === 'nota_credito') &&
        invoice.origin !== 'apertura' &&
        !invoice.selfBilling &&
        !invoice.void;
}
/** Left to collect or to pay at the end of `day` (negative for a credit note). */
export function openAt(invoice, day) {
    if (invoice.void || invoice.date > day)
        return 0;
    const paid = invoice.settlements
        .filter((item) => item.date <= day)
        .reduce((sum, item) => sum + toCents(item.amount), 0);
    return toCents(invoice.netDue) - paid;
}
/** What is left, by due date: the latest dues are the ones still open. */
function openDues(invoice, open) {
    const dues = [...invoice.dues]
        .filter((due) => toCents(due.amount) !== 0)
        .sort((a, b) => a.dueDate.localeCompare(b.dueDate));
    // A credit note, or no dues: all due on the date of the document.
    if (open < 0 || !dues.length)
        return [{ dueDate: dues[0]?.dueDate ?? invoice.date, cents: open }];
    const parts = [];
    let left = open;
    for (let index = dues.length - 1; index >= 0 && left > 0; index -= 1) {
        const due = dues[index];
        const take = index === 0 ? left : Math.min(left, toCents(due.amount));
        if (take > 0)
            parts.push({ dueDate: due.dueDate, cents: take });
        left -= take;
    }
    return parts;
}
function bucketOf(dueDate, day) {
    const late = Math.round((parseIsoDate(day).getTime() - parseIsoDate(dueDate).getTime()) / 86_400_000);
    if (late <= 0)
        return 'notDue';
    if (late <= 30)
        return 'd30';
    if (late <= 60)
        return 'd60';
    if (late <= 90)
        return 'd90';
    return 'over90';
}
/** The part of the taxable in `cents` of what is due: the report is without VAT. */
function withoutVat(invoice, cents) {
    const due = toCents(invoice.netDue);
    return due === 0 ? 0 : Math.round((cents * toCents(invoice.taxable)) / due);
}
/** Open at `day` without VAT, by how late each due is. */
export function agingAt(invoices, day) {
    const cents = Object.fromEntries(AGING_BUCKETS.map((bucket) => [bucket, 0]));
    for (const invoice of invoices) {
        const open = openAt(invoice, day);
        if (open === 0)
            continue;
        for (const part of openDues(invoice, open)) {
            cents[bucketOf(part.dueDate, day)] += withoutVat(invoice, part.cents);
        }
    }
    const total = AGING_BUCKETS.reduce((sum, bucket) => sum + cents[bucket], 0);
    return {
        total: fromCents(total),
        overdue: fromCents(total - cents.notDue),
        buckets: Object.fromEntries(AGING_BUCKETS.map((bucket) => [bucket, fromCents(cents[bucket])])),
    };
}
export function isReceivable(invoice) {
    return (invoice.direction === 'emessa' &&
        invoice.kind !== 'proforma' &&
        !invoice.selfBilling &&
        !invoice.void);
}
export function isPayable(invoice) {
    return invoice.direction === 'ricevuta' && !invoice.selfBilling && !invoice.void;
}
/** The days of the trend: every `days` from `from`, and always `to`. */
export function trendDays(from, to, days) {
    const result = [];
    for (let day = from; day < to; day = addDays(day, days))
        result.push(day);
    result.push(to);
    return result;
}
function trendPoint(receivables, payables, date) {
    const owed = agingAt(receivables, date);
    const due = agingAt(payables, date);
    return {
        date,
        receivables: owed.total,
        receivablesOverdue: owed.overdue,
        payables: due.total,
        payablesOverdue: due.overdue,
    };
}
/** Taxable of one side, by month, against the year before. */
function series(documents, year, months, period, today) {
    const sum = (items) => items.reduce((total, item) => total + toCents(item.taxable), 0);
    const inMonth = (target, month, upTo = false) => documents.filter((item) => {
        if (!item.date.startsWith(`${target}-`))
            return false;
        const of = Number(item.date.slice(5, 7));
        return upTo ? of <= month : of === month;
    });
    const currentMonth = today.startsWith(`${year}-`) ? Number(today.slice(5, 7)) : null;
    return {
        total: fromCents(sum(documents.filter((item) => item.date >= period.from && item.date <= period.asOf))),
        previous: fromCents(sum(documents.filter((item) => item.date >= yearBefore(period.from) && item.date <= yearBefore(period.asOf)))),
        months: months.map((month) => ({
            month,
            amount: fromCents(sum(inMonth(year, month))),
            previous: fromCents(sum(inMonth(year - 1, month))),
            progressive: fromCents(sum(inMonth(year, month, true))),
            previousProgressive: fromCents(sum(inMonth(year - 1, month, true))),
            invoices: inMonth(year, month).filter((item) => item.kind === 'fattura').length,
            current: month === currentMonth,
            future: `${year}-${String(month).padStart(2, '0')}` > today.slice(0, 7),
        })),
    };
}
/** The dashboard of a period, seen on `today`. */
export function reportDashboard(invoices, year, quarter, today, banks = null, includeIntercompany = false) {
    const { from, to, months } = dashboardPeriod(year, quarter);
    const asOf = today < to ? today : to;
    const receivables = invoices.filter(isReceivable);
    const payables = invoices.filter(isPayable);
    const step = quarter ? 'giorno' : 'settimana';
    const days = from <= asOf ? trendDays(from, asOf, step === 'giorno' ? 1 : 7) : [];
    const economic = (direction) => invoices.filter(isEconomic(direction));
    const counted = (direction) => economic(direction).filter((item) => includeIntercompany || !item.intercompany);
    const intergroup = (direction) => economic(direction).filter((item) => item.intercompany && item.date >= from && item.date <= asOf);
    const total = (items) => fromCents(items.reduce((sum, item) => sum + toCents(item.taxable), 0));
    return {
        year,
        quarter: quarter ?? null,
        from,
        to,
        asOf,
        revenue: series(counted('emessa'), year, months, { from, asOf }, today),
        purchases: series(counted('ricevuta'), year, months, { from, asOf }, today),
        receivables: agingAt(receivables, asOf),
        payables: agingAt(payables, asOf),
        trend: {
            step,
            points: days.map((day) => trendPoint(receivables, payables, day)),
            previous: days.map((day) => trendPoint(receivables, payables, yearBefore(day))),
        },
        banks,
        intercompany: {
            included: includeIntercompany,
            count: intergroup('emessa').length + intergroup('ricevuta').length,
            revenue: total(intergroup('emessa')),
            purchases: total(intergroup('ricevuta')),
        },
    };
}
//# sourceMappingURL=report-dashboard.js.map